<template>
  <div
    class="material-card folder-card"
    :class="{ 'is-drop-target': isDropTarget }"
    @click="handleClick"
    @dragover.prevent="$emit('dragover', folder.id, $event)"
    @dragleave="$emit('dragleave', folder.id)"
    @drop.prevent.stop="handleDrop"
  >
    <div class="mat-thumb folder-thumb-container">
      <div class="folder-thumb">
        <svg width="48" height="48" viewBox="0 0 24 24" aria-hidden="true">
          <path fill="#FBC02D" d="M10 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z"/>
          <path fill="#FDD835" d="M4 8h16v10H4z"/>
          <path fill="#F9A825" d="M2 6c0-1.1.9-2 2-2h6l2 2H4v12c-1.1 0-2-.9-2-2V6z" opacity=".35"/>
        </svg>
      </div>
      <div v-if="canManage" class="card-actions">
        <button type="button" class="btn-action" @click.stop="$emit('rename', folder)">重命名</button>
        <button
          type="button"
          class="btn-action danger icon-action"
          title="删除文件夹"
          :aria-label="`删除文件夹${folder.name}`"
          @click.stop="$emit('delete', folder)"
        ><Trash2 :size="13" /></button>
      </div>
    </div>
    <div class="mat-body">
      <span class="material-name" :title="folder.name">{{ folder.name }}</span>
      <span class="material-cat">{{ count }} 个素材</span>
    </div>
  </div>
</template>

<script setup>
import { Trash2 } from '@lucide/vue';

const props = defineProps({
  folder: {
    type: Object,
    required: true,
  },
  count: {
    type: Number,
    default: 0,
  },
  canManage: {
    type: Boolean,
    default: false,
  },
  isDropTarget: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['click', 'rename', 'delete', 'dragover', 'dragleave', 'drop']);

let dropSuppressed = false;

function handleDrop(event) {
  dropSuppressed = true;
  setTimeout(() => { dropSuppressed = false; }, 400);
  emit('drop', event);
}

function handleClick() {
  if (dropSuppressed) return;
  emit('click', props.folder.id);
}
</script>

<style scoped>
.material-card {
  border-radius: 12px;
  border: 1px solid var(--border-color, #e2e8f0);
  background: #ffffff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  position: relative;
  cursor: pointer;
}

.material-card:hover {
  border-color: var(--color-primary);
  transform: translateY(-2px);
  box-shadow: 0 10px 20px -3px rgba(15, 23, 42, 0.08);
}

.folder-card.is-drop-target {
  border-color: var(--color-primary);
  border-width: 2px;
  box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.18);
  transform: translateY(-2px);
}

.mat-thumb {
  width: 100%;
  aspect-ratio: 4 / 3;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  border-bottom: 1px solid #f1f5f9;
}

.folder-thumb-container {
  background: linear-gradient(145deg, #fffdf5 0%, #fef3c7 100%);
}

.folder-thumb {
  width: 64px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
}

.folder-card:hover .folder-thumb {
  transform: scale(1.05);
}

.card-actions {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 5;
  display: flex;
  gap: 4px;
  opacity: 0;
  transform: translateY(-4px);
  transition: opacity 0.18s ease, transform 0.18s ease;
  pointer-events: none;
}

.material-card:hover .card-actions,
.material-card:focus-within .card-actions {
  opacity: 1;
  transform: translateY(0);
  pointer-events: auto;
}

.btn-action {
  padding: 3px 10px;
  font-size: 11px;
  border-radius: 999px;
  border: 1px solid var(--border-color, #cbd5e1);
  background: #ffffff;
  color: var(--text-dark, #0f172a);
  box-shadow: 0 2px 4px rgba(15, 23, 42, 0.08);
  white-space: nowrap;
  cursor: pointer;
}

.btn-action:hover {
  filter: brightness(0.97);
}

.btn-action.danger {
  border-color: #fecaca;
  background: #fff7f7;
  color: #dc2626;
}

.btn-action.icon-action {
  width: 26px;
  height: 26px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.mat-body {
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  background: #ffffff;
}

.material-name {
  font-size: 13px;
  font-weight: 700;
  color: var(--text-dark, #0f172a);
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.material-cat {
  font-size: 11px;
  color: var(--text-muted, #64748b);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
